/**
 * Circle Activity Feed Component
 * 
 * Shows recent activity from user's accountability circle on the dashboard.
 * Displays messages and check-ins from circle sisters to encourage engagement.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { 
  Users, MessageCircle, CheckCircle2, Sparkles, 
  ChevronRight, Loader2, Trophy, Heart
} from 'lucide-react';

const API_URL = window.location.origin;

const CircleActivityFeed = ({ className = '' }) => {
  const [activityData, setActivityData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivityFeed();
  }, []);

  const fetchActivityFeed = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/accountability-circle/activity-feed?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivityData(response.data);
    } catch (error) {
      console.error('Failed to fetch circle activity:', error);
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Card className={`card-base ${className}`} data-testid="circle-activity-feed">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-purple-600" />
        </CardContent>
      </Card>
    );
  }

  // No circle - don't show anything (the main widget handles join prompts)
  if (!activityData?.has_circle) {
    return null;
  }

  // Has circle but no activity yet
  if (!activityData?.has_activity) {
    return (
      <Card className={`card-base ${className}`} data-testid="circle-activity-feed">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Circle Sisters' Updates
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <div className="h-16 w-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
              <MessageCircle className="h-8 w-8 text-purple-400" />
            </div>
            <p className="text-gray-600 text-sm mb-3">
              Your circle is quiet right now. Be the first to share!
            </p>
            <Link to={`/accountability-circle/${activityData.circle.id}`}>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white">
                <MessageCircle className="h-4 w-4 mr-2" />
                Start a Conversation
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Activity type icons
  const getActivityIcon = (type) => {
    switch (type) {
      case 'weekly_checkin':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'checkin':
        return <Heart className="h-4 w-4 text-pink-500" />;
      case 'message':
      default:
        return <MessageCircle className="h-4 w-4 text-purple-500" />;
    }
  };

  return (
    <Card className={`card-base ${className}`} data-testid="circle-activity-feed">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg text-forest flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            Circle Sisters' Updates
          </CardTitle>
          <Link to={`/accountability-circle/${activityData.circle.id}`}>
            <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-700 hover:bg-purple-50">
              View All <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          See what your sisters in <span className="font-medium text-purple-600">{activityData.circle.name}</span> have been up to
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {activityData.activity.map((item, idx) => (
            <div 
              key={item.id || idx}
              className="flex items-start gap-3 p-3 rounded-lg bg-gradient-to-r from-purple-50/50 to-transparent hover:from-purple-50 transition-colors"
            >
              <div className="relative">
                <Avatar className="h-10 w-10 border-2 border-white shadow-sm">
                  <AvatarImage 
                    src={item.user?.profile_image?.startsWith('http') 
                      ? item.user.profile_image 
                      : (item.user?.profile_image ? `${API_URL}${item.user.profile_image}` : undefined)} 
                  />
                  <AvatarFallback className="bg-purple-100 text-purple-600 text-sm font-medium">
                    {item.user_name?.charAt(0) || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-white flex items-center justify-center shadow-sm">
                  {getActivityIcon(item.type)}
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-medium text-forest">
                    {item.action_text}
                  </p>
                  {item.wins_count > 0 && (
                    <Badge className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0">
                      <Trophy className="h-3 w-3 mr-0.5" />
                      {item.wins_count}
                    </Badge>
                  )}
                </div>
                
                {item.content_preview && (
                  <p className="text-sm text-gray-600 line-clamp-2 italic">
                    "{item.content_preview}"
                  </p>
                )}
                
                <p className="text-xs text-gray-400 mt-1">
                  {item.time_ago}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Quick action to engage */}
        <div className="mt-4 pt-4 border-t border-gray-100">
          <Link to={`/accountability-circle/${activityData.circle.id}`}>
            <Button 
              variant="outline" 
              className="w-full border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-300"
            >
              <MessageCircle className="h-4 w-4 mr-2" />
              Join the Conversation
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

export default CircleActivityFeed;
